import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline'

type Props = {
  title: React.ReactNode
  width?: string
  maxHeight?: string
  onClose: () => void
  onBack?: () => void
  headerRight?: React.ReactNode
  footer?: React.ReactNode
  /** Wraps children in the standard padded scroll area; turn off to lay the body out yourself. */
  padded?: boolean
  children: React.ReactNode
}

export default function Modal({
  title,
  width = 'max-w-xl',
  maxHeight = 'max-h-[90vh]',
  onClose,
  onBack,
  headerRight,
  footer,
  padded = true,
  children,
}: Props) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  // Portalled: a transformed ancestor (the sidebar) would otherwise become the
  // containing block for `fixed` and clip the dialog inside it.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className={`flex ${maxHeight} w-full ${width} flex-col overflow-hidden rounded-xl bg-white shadow-2xl`}>
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex min-w-0 items-center gap-3">
            {onBack && (
              <button type="button" aria-label="Back" onClick={onBack} className="rounded p-1 transition hover:bg-gray-100">
                <ArrowLeftIcon className="size-4 text-gray-600" />
              </button>
            )}
            <h2 className="flex min-w-0 items-center gap-1 text-lg font-medium text-gray-900">{title}</h2>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {headerRight}
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="rounded p-1 transition hover:bg-gray-100"
            >
              <XMarkIcon className="size-5 text-gray-500" />
            </button>
          </div>
        </div>

        {padded ? (
          <div className="flex-1 overflow-y-auto px-6 py-4 [scrollbar-width:thin]">{children}</div>
        ) : (
          children
        )}

        {footer && <div className="shrink-0 border-t border-gray-100 px-6 py-4">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
